import type { LazyExoticComponent, ComponentType } from 'react'

/** One row in the hosted plugin chrome subnav (mirrors desktop {@link import('@owlbert/plugin-sdk').ModuleRoute} labels). */
export type HostedPluginWebSubroute = { label: string; suffix: string }

export type HostedPluginPage = LazyExoticComponent<ComponentType<unknown>>

export type HostedPluginRegistration = {
  /** First path segment under `/plugins/:id/` when visiting `/plugins/:id` (index redirect). */
  defaultSuffix: string
  subroutes: readonly HostedPluginWebSubroute[]
  /** Keys are path suffixes under `/plugins/:id/` (no leading slash); may contain `/` for nested paths. */
  pages: Readonly<Record<string, HostedPluginPage>>
}

const builtins = new Map<string, HostedPluginRegistration>()
const overlays = new Map<string, HostedPluginRegistration>()

/** Built-in registrations (shipped with owlbert-web). */
export function defineHostedPluginWeb(pluginId: string, registration: HostedPluginRegistration): void {
  builtins.set(pluginId, registration)
}

/**
 * Optional runtime overlay (tests, experiments) or future dynamic registration.
 * Replaces the built-in registration for the same `pluginId` when present.
 */
export function registerHostedPluginWeb(pluginId: string, registration: HostedPluginRegistration): void {
  overlays.set(pluginId, registration)
}

export function unregisterHostedPluginWeb(pluginId: string): void {
  overlays.delete(pluginId)
}

export function getHostedPluginRegistration(pluginId: string): HostedPluginRegistration | undefined {
  return overlays.get(pluginId) ?? builtins.get(pluginId)
}

export function listDefinedHostedPluginIds(): string[] {
  const ids = new Set<string>([...builtins.keys(), ...overlays.keys()])
  return [...ids].sort((a, b) => a.localeCompare(b))
}
